<template>
  <!-- Lista de capas PURA (sin shell ni posicionamiento): cabecera con contador,
       buscador (si hay muchas), y filas con swatch + nombre (click = activar) +
       acciones (info / visibilidad). Se monta dentro de un contenedor que da el
       chrome (pestaña "Capas" del WidgetSidebar). Props down, events up. -->
  <div class="h-full flex flex-col">
    <template v-if="layers && layers.length">
      <!-- Cabecera: título + contador visibles/total. -->
      <div class="flex items-center justify-between mb-2 px-1 flex-none">
        <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{{ $t('layers') }}</span>
        <span class="text-[11px] font-medium text-gray-500 bg-[#f2f2f7] rounded-full px-2 py-0.5"
          :title="$t('layers')">
          {{ visibleCount }}/{{ layers.length }}
        </span>
      </div>

      <!-- Buscador (solo cuando hay muchas capas). -->
      <b-input
        v-if="showSearch"
        v-model="query"
        type="search"
        :placeholder="$t('search')"
        icon-pack="fas"
        icon="magnifying-glass"
        size="is-small"
        rounded
        class="mb-2 flex-none" />

      <!-- Lista (scroll propio → la cabecera/buscador quedan fijos). -->
      <ul v-if="filteredLayers.length" class="epc-layers-scroll flex-1 min-h-0 overflow-y-auto space-y-0.5 -mr-1 pr-1">
        <li v-for="lyr in filteredLayers" :key="lyr.name"
            :class="[
              'group relative flex items-center gap-2.5 pl-3 pr-1 py-2 rounded-lg transition-colors duration-150',
              lyr.name === activeName
                ? 'bg-[#eef3fb]'
                : (locked ? '' : 'hover:bg-[#f4f4f6]'),
            ]">
          <!-- Barra de acento (capa activa), estilo Figma/VSCode. -->
          <span v-if="lyr.name === activeName"
            class="absolute left-1 top-2 bottom-2 w-[3px] rounded-full bg-[#3a6bc8]"></span>

          <!-- Cuadradito de color + nombre — click activa la capa para
               filtrar los clicks sobre el viewer (solo la activa responde).
               Si locked (workLayer en la URL), el click es no-op. Capa oculta →
               atenuada para que se note que está apagada. -->
          <div :class="[
                 'flex items-center gap-2.5 min-w-0 flex-1 transition-opacity duration-150',
                 locked ? 'cursor-default' : 'cursor-pointer',
                 lyr.isVisible ? '' : 'opacity-50',
               ]"
               @click="_ActivateLayer(lyr)">
            <span class="w-3.5 h-3.5 rounded-[4px] flex-shrink-0 ring-1 ring-inset ring-black/10 shadow-sm"
                  :style="{ backgroundColor: _GetCssColor(lyr.color) }"></span>
            <span class="text-[13px] truncate min-w-0 flex-1"
                  :class="lyr.name === activeName ? 'text-[#2f5aad] font-semibold' : 'text-gray-700'"
                  :title="lyr.displayName || lyr.name">
              {{ lyr.displayName || lyr.name }}
            </span>
            <!-- Candado al lado del nombre cuando la capa está fijada por
                 workLayer en la URL — fuera del span truncado para que no
                 se recorte si el nombre es largo. -->
            <b-icon v-if="locked && lyr.name === activeName"
              pack="fas" icon="lock" size="is-small" class="text-[#3a6bc8] flex-shrink-0" />
          </div>

          <!-- Acciones de la fila como chip FLOTANTE (overlay) → el nombre ocupa
               todo el ancho. Aparecen al hover/focus; se dejan visibles si la capa
               está oculta (para reactivarla) o si el panel está fijado. -->
          <div class="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center p-0.5
                      rounded-lg bg-white shadow-sm ring-1 ring-black/5 transition-opacity duration-150"
            :class="(actionsAlwaysVisible || !lyr.isVisible)
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'">
            <button @click.stop="_LayerInfo(lyr)"
              class="w-7 h-7 flex items-center justify-center rounded-md text-gray-400
                     hover:text-[#3a6bc8] hover:bg-[#e8eef8] transition-colors duration-150"
              :aria-label="$t('layer_info')" :title="$t('layer_info')">
              <b-icon icon="circle-info" pack="fas" size="is-small" />
            </button>

            <button @click.stop="_ToggleLayer(lyr)"
              :class="[
                'w-7 h-7 flex items-center justify-center rounded-md transition-colors duration-150',
                'hover:text-[#3a6bc8] hover:bg-[#e8eef8]',
                lyr.isVisible ? 'text-gray-400' : 'text-gray-300',
              ]">
              <b-icon :icon="lyr.isVisible ? 'eye' : 'eye-slash'" pack="fas" size="is-small" />
            </button>
          </div>
        </li>
      </ul>

      <!-- Sin resultados de búsqueda. -->
      <div v-else class="flex-1 flex flex-col items-center justify-center text-center py-6 text-gray-400">
        <b-icon pack="fas" icon="magnifying-glass" size="is-small" class="mb-2 text-gray-300" />
        <p class="text-sm">{{ $t('no_results') }}</p>
      </div>
    </template>

    <!-- Empty state: sin capas. -->
    <div v-else class="flex-1 flex flex-col items-center justify-center text-center py-8 text-gray-400">
      <b-icon pack="fas" icon="layer-group" custom-size="fa-2x" class="mb-2 text-gray-300" />
      <p class="text-sm">{{ $t('no_layers_to_display') }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "LayersList",

  props: {
    // [{name, displayName, color, isVisible}] — el padre maneja la mutación
    layers: { type: Array, default: () => [] },
    // Nombre de la capa activa (la única que responde a clicks sobre el viewer).
    // null = todas las capas trackeables son clickeables (comportamiento default).
    activeName: { type: String, default: null },
    // Si true, la capa activa está fijada (típicamente porque viene en la URL
    // como `wl`) y el usuario no puede cambiarla.
    locked: { type: Boolean, default: false },
    // Si true, el chip de acciones (info/visibilidad) se muestra siempre, sin
    // esperar al hover (p. ej. cuando el panel está fijado/acoplado).
    actionsAlwaysVisible: { type: Boolean, default: false },
  },

  data() {
    return {
      query: "",   // filtro del buscador (local a la lista)
    }
  },

  computed: {
    /** Nº de capas actualmente visibles (para el contador de la cabecera). */
    visibleCount() {
      return (this.layers || []).filter(l => l.isVisible).length
    },

    /** Muestra el buscador solo cuando la lista es larga (progressive disclosure). */
    showSearch() {
      return (this.layers || []).length > 6
    },

    /** Capas filtradas por el buscador (por nombre visible o técnico). */
    filteredLayers() {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.layers || []
      return (this.layers || []).filter(l =>
        (l.displayName || l.name || "").toLowerCase().includes(q)
      )
    },
  },

  methods: {
    _ToggleLayer(layer) {
      // Solo emite. El padre es el único que muta el array (props down, events up).
      this.$emit('toggleLayer', layer, !layer.isVisible)
    },

    /** Pide al host abrir el detalle (modal) de esta capa. */
    _LayerInfo(layer) {
      this.$emit('layer-info', layer)
    },

    /**
     * Click sobre el nombre/swatch — activa la capa. Si ya estaba activa,
     * la desactiva (vuelve al modo "todas clickeables"). Si está lockada
     * (por workLayer/url) es no-op.
     */
    _ActivateLayer(layer) {
      if (this.locked) return
      const next = layer.name === this.activeName ? null : layer.name
      this.$emit('activate-layer', next)
    },

    _GetCssColor(value) {
      let s = Number(value || 0).toString(16)
      while (s.length < 6) s = '0' + s
      return '#' + s
    },
  },
}
</script>

<style scoped>
/* Scrollbar fina y sutil para la lista (coherente con el cuerpo del sidebar). */
.epc-layers-scroll {
  scrollbar-width: thin;
  scrollbar-color: #d4d4dd transparent;
}

.epc-layers-scroll::-webkit-scrollbar {
  width: 10px;
}

.epc-layers-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.epc-layers-scroll::-webkit-scrollbar-thumb {
  background-color: #d4d4dd;
  border-radius: 999px;
  border: 3px solid transparent;
  background-clip: padding-box;
}

.epc-layers-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #b9b9c6;
}
</style>
